import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Rx';
import { Subscription } from 'rxjs/Subscription';
import { BlockUIService } from 'ng-block-ui';
import * as _ from 'lodash';
import * as stageTypes from '../../types';
import * as animations from '../../../shared/animations';
import * as commonTypes from '../../../shared/types';
import { StageSandbox } from '../../services/stage.sandbox';
import { IApplicationState } from '../../../stateManagement/states/applicationState';
import { IDynamicData } from '../../../shared/types/dynamicData';
import { QUESTION_TYPES, BUTTON_TYPES } from '../../types/constants';

enum WelcomeSubPages {
    Attachments = 1,
    Accept = 2,
    Query = 3,
    Decline = 4
}
@Component({
  selector: 'tr-welcome-container',
  animations: [animations.fadeInUpAnimation],
  templateUrl: './welcome-container.component.html',
  //styleUrls: ['./welcome-container.component.scss']
})
export class WelcomeContainerComponent implements OnInit, OnDestroy {
  @Input() entity: stageTypes.IPage;
  private isSubmitted: boolean = false;
  private subscriptions: Array<Subscription> = [];
  private htmlContents: stageTypes.IPageDef[];
  private attachmentContents: stageTypes.IPageDef[] = [];
  private allAttachmentsDownloaded$: Observable<boolean> = this.sb.getAllAttachmentsDownloadingStatus$();
  private subPage: WelcomeSubPages = WelcomeSubPages.Attachments;
  private queryOfferMessage: string = '';
  private blockUI_target = 'stage-section';
  // TODO: remove in real business logic
  private updatedStageId: number;
  private isAlreadyAccepted: boolean = false;
    // 20/02/2022 Anopan Kandiah HRSC-7167: "offerIntroduction" is no longer used. See file "welcome-container.component.html" for the content used.
  private staticText = {
    offerIntroduction: `
      <p>Before we can let you go any further you need to check your offer. You can do this by clicking on the attachment(s) below. If everything looks right, you can press the <b>Accept</b> button and continue. Otherwise, you can <b>Query</b> the offer.</p>
      <div id="accordion" role="tablist" aria-multiselectable="true">
      <div class="expandHeading">
         <a class="small" data-toggle="collapse" data-parent="#accordion" href="#expandDetail" aria-expanded="true" aria-controls="expandDetail" href="javascript:void(0)">+ Click here if you would like more information on accepting or querying your offer.</a>
      </div>
      <div id="expandDetail" class="collapse" role="tabpanel" aria-labelledby="expandHeading">
         <p>If you're happy with the offer then click the <strong>Accept</strong> button to proceed. Once accepted you'll be able to continue on and complete the additional details that we need to get you started! We'll also give you an opportunity to print out a signed copy of the offer (and other documents) at the end of this process.</p>
         <p>If something's not quite right with your offer, press the <strong>Query</strong> button. We'll then point you in the right direction to get this resolved!</p>
      </div>
     </div>
    `,
      // 29/11/2023 Anopan Kandiah ADO-117965: Changed header from "Congratulations!" to "Thank you.".
    acceptCongratulation: {
      header: `Thank you.`,
      content: `
      <p>
        You can continue by pressing the 'Next' button.
      </p>
      `
    },
      // 03/03/2022 Anopan Kandiah HRSC-7245: "acceptTooltipDisabled" and "acceptTooltip" are no longer used. See file "welcome-container.component.html" for the content used.
   acceptTooltipDisabled: 'You need to view your offer documents before you can proceed.  Once you have viewed your offer documents you can click this button to accept the offer and proceed to the next step.',
    acceptTooltip: 'If you’re happy with the offer of employment we have made, then this button will let you accept and proceed!',
    queryTooltip: 'If something isn’t quite right with your offer, then please click the Query button.',
    declineOfferTooltip: 'You can formally decline your offer by clicking this decline button. Please speak to us first or use the Query button if you would like to clarify this offer.'
  };

  constructor(private sb: StageSandbox, private blockUI: BlockUIService) { }

  ngOnInit() {
    // we only get 2 first items for html content
    this.htmlContents = this.entity.fields.filter((field: stageTypes.IPageDef) => field.type === QUESTION_TYPES.HTML_CONTENT);
    // get all attachments
    for (let index = 0; index < this.entity.fields.length; index++) {
      let field = this.entity.fields[index];
      if (field.type === QUESTION_TYPES.ATTACHMENTS) {
        this.attachmentContents.push(Object.assign({}, field, { questionId: index }));
      }
    }
  }
  ngOnDestroy() {
    // Called once, before the instance is destroyed.
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
  download(questionIndex: number) {
    this.subscriptions.push(
      this.sb.downloadAttachment(questionIndex)
    );
  }
  fileIsRead$(questionIndex: number): Observable<boolean> {
    return this.sb.getFileReadingStatus$(questionIndex);
  }
  getFileContent$(questionIndex: number): Observable<any> {
    return this.sb.getFileContent$(questionIndex);
  }
  isDownloading$(questionIndex: number): Observable<boolean> {
    return this.sb.getFileDownloadingStatus$(questionIndex, 'download');
  }
  lastItemOfOddArray(index: number): boolean {
    return (this.attachmentContents.length % 2 === 0) ? false : (index === (this.attachmentContents.length - 1 ));
  }
  onButtonClicked(event): void {
    event.stopPropagation();

    const textContent = ('textContent' in document) ? 'textContent' : 'innerText';

    let buttonType = null;

    // Check if attribute exists
    if (event.target.getAttribute('name') !== 'undefined' && event.target.getAttribute('name') !== null) {
      // 17/02/2022 Anopan Kandiah HRSC-7167: Get the clicked button's "name" attribute value (e.g. Accept).
      buttonType = String(event.target.getAttribute('name').toUpperCase());
      console.log(1);
    } else {
      buttonType = String(event.currentTarget[textContent].trim().toUpperCase());
      console.log(2);
    }

    switch (buttonType) {
      case BUTTON_TYPES.ACCEPT:
        // A guard here, in case user manually remove attachment-disabled class to be able to trigger click event
       if ( !this.isAlreadyAccepted ) {
           this.allAttachmentsDownloaded$.take(1).subscribe(allDownloaded => {
               if (allDownloaded) {
                   this.isAlreadyAccepted = true;

                   this.subscriptions.push(
                       this.sb.offerSelected$(buttonType)
                           .subscribe((response: commonTypes.IResponse) => {
                               this.subPage = WelcomeSubPages.Accept;
                               // TODO: updated currentStageId if it is returned from response
                               // this value will be used to dispatch UpdateStageId action when NEXT button in the subpage is clicked
                               // However this one will be removed as user shouldn't be able to navigate straight to Personal detail page
                               // until they get a call from HR
                               if (_.has(response, 'stage')) {
                                   this.updatedStageId = Number(response['stage']);
                               }
                           })
                   );
               }
           });
       }

      break;
      case BUTTON_TYPES.QUERY:
        this.queryOfferMessage = '';
        this.subPage = WelcomeSubPages.Query;
      break;
      case BUTTON_TYPES.BACK:
      this.isSubmitted = false;
        this.subPage = WelcomeSubPages.Attachments;
     break;
      case BUTTON_TYPES.SEND_MESSAGE:
        this.subscriptions.push(
          this.sb.offerSelected$(buttonType, { message: this.queryOfferMessage })
            .subscribe((response: commonTypes.IResponse) => {
              this.subPage = WelcomeSubPages.Query;
            }, (error) => {
              console.log(`Cannot execute Query Offer action (message: ${this.queryOfferMessage}). Error: ${error}`);
            })
        );
      this.isSubmitted = true;
      break;
      case BUTTON_TYPES.NEXT:
        this.sb.resetCurrentStageId();
      break;
      case BUTTON_TYPES.DECLINE:
        this.subPage = WelcomeSubPages.Decline;
      break;
      case BUTTON_TYPES.CONTINUE_DECLINE:
        this.blockUI.start(this.blockUI_target, 'Please Wait...');
        this.subscriptions.push(
          this.sb.declineCurrentStage$()
            .subscribe((response: commonTypes.IResponse) => {
              this.blockUI.stop(this.blockUI_target);
              this.sb.resetCurrentStageId();
            }, (error) => {
              console.log(`Cannot execute Continue Decline action. Error: ${error}`);
            })
        );
      break;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/stage/containers/welcome-container/welcome-container.component.ts